var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState, } from 'react';
import classNames from 'classnames';
import DetectOutsideClick from '@anwb/detect-outside-click';
import Icon from '@anwb/icon';
import { Portal } from '@anwb/portal';
import useIsScrollable from '@anwb/use-is-scrollable';
import { ModalClose, ModalContainer, ModalContent, ModalFooter, ModalFooterButton, ModalHeader, ModalTitle, ModalWrapper, } from './styles/modal.styled';
/**
 * A modal is a lightbox displaying content on top of the current page
 */
const Modal = (_a) => {
    var _b;
    var { children, variant = 'passive', size = 's', short, fullSize, buttonText = 'Button text', buttonVisualStyle = 'primary', cancelButtonText = 'Button text', buttonHandler, onOpen, onClose, open: openProp = true, class: className, title } = _a, props = __rest(_a, ["children", "variant", "size", "short", "fullSize", "buttonText", "buttonVisualStyle", "cancelButtonText", "buttonHandler", "onOpen", "onClose", "open", "class", "title"]);
    const bodySettings = useRef(undefined);
    const [open, setOpen] = useState(openProp);
    const modalContentRef = useRef(null);
    const { isScrollable, isScrolled } = useIsScrollable(modalContentRef);
    const [showFade, setShowFade] = useState(isScrollable);
    const modalClassNames = classNames('PONCHO-modal', className);
    const footerRef = useRef(null);
    const resetScrollBehavior = () => {
        if (bodySettings.current !== undefined) {
            const scrollContainer = (document.scrollingElement ||
                document.documentElement);
            document.body.style.cssText = bodySettings.current.styles;
            scrollContainer.style.scrollBehavior = 'auto';
            scrollContainer.scrollTop = bodySettings.current.scrollTop;
            scrollContainer.style.scrollBehavior = bodySettings.current.scrollBehavior;
            bodySettings.current = undefined;
        }
    };
    useEffect(() => setShowFade(isScrollable), [isScrollable]);
    useEffect(() => setOpen(openProp), [openProp]);
    useEffect(() => {
        const escapeHandler = ({ keyCode }) => keyCode === 27 && setOpen(false);
        const addEscapeListener = () => document.addEventListener('keydown', escapeHandler);
        const removeEscapeListener = () => document.removeEventListener('keydown', escapeHandler);
        const scrollContainer = (document.scrollingElement || document.documentElement);
        if (open) {
            const hasVerticalScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;
            bodySettings.current = {
                scrollTop: scrollContainer.scrollTop,
                scrollBehavior: scrollContainer.style.scrollBehavior,
                styles: document.body.style.cssText,
            };
            document.body.style.cssText = `; overflow-x: hidden; overflow-y: ${hasVerticalScroll ? 'scroll' : 'hidden'}; position: fixed; top: -${bodySettings.current.scrollTop}px; height: 100%; width: 100%;`;
            addEscapeListener();
            if (onOpen) {
                onOpen();
            }
        }
        else {
            resetScrollBehavior();
            removeEscapeListener();
            if (onClose) {
                onClose();
            }
        }
        return () => {
            resetScrollBehavior();
            removeEscapeListener();
        };
    }, [open]);
    const handleScroll = (node) => {
        const { scrollHeight, scrollTop, clientHeight } = node;
        setShowFade(scrollHeight - scrollTop !== clientHeight);
    };
    const footerAffirmativeButtonOnClick = () => {
        if (buttonHandler) {
            buttonHandler();
        }
        else {
            setOpen(false);
        }
    };
    const footerAffirmativeButtonOnKeyDown = ({ key }) => {
        if (key === 'Enter') {
            footerAffirmativeButtonOnClick();
        }
    };
    const footerDismissiveButtonOnKeyDown = ({ key }) => {
        if (key === 'Enter')
            setOpen(false);
    };
    if (!open) {
        return null;
    }
    return (React.createElement(Portal, { id: "modal", className: "PONCHO-modal-container" },
        React.createElement(ModalContainer, Object.assign({ className: modalClassNames }, props),
            React.createElement(DetectOutsideClick, { onClickOutside: () => setOpen(false) },
                React.createElement(ModalWrapper, { className: "PONCHO-modal__wrapper", "$size": size, "$fullSize": fullSize, "$short": short, "$showFade": showFade, "$fadeOffset": ((_b = footerRef === null || footerRef === void 0 ? void 0 : footerRef.current) === null || _b === void 0 ? void 0 : _b.scrollHeight) || 0 },
                    React.createElement(ModalHeader, { className: "PONCHO-modal__header", "$fullSize": fullSize, "$isScrollable": isScrollable, "$isScrolled": isScrolled },
                        title && React.createElement(ModalTitle, { variant: "component-title" }, title),
                        React.createElement(ModalClose, { className: "PONCHO-modal__close", onClick: () => setOpen(false), type: "button" },
                            React.createElement(Icon, { variant: "cross", size: "sm" }))),
                    React.createElement(ModalContent, Object.assign({ ref: modalContentRef, className: "PONCHO-modal__content" }, (isScrollable
                        ? {
                            onScroll: (event) => handleScroll(event.target),
                        }
                        : {}), { "$fullSize": fullSize, "$isScrollable": isScrollable }), children),
                    variant === 'dialog' && (React.createElement(ModalFooter, { ref: footerRef, className: "PONCHO-modal__footer", "$isScrollable": isScrollable, "$isScrolled": isScrolled },
                        React.createElement(ModalFooterButton, { size: "small", color: "tertiary", onClick: () => setOpen(false), onKeyDown: footerDismissiveButtonOnKeyDown }, cancelButtonText),
                        React.createElement(ModalFooterButton, { size: "small", color: buttonVisualStyle || 'secondary', onClick: footerAffirmativeButtonOnClick, onKeyDown: footerAffirmativeButtonOnKeyDown }, buttonText))))))));
};
export default Modal;
//# sourceMappingURL=Modal.js.map